import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react'
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material'
import ViewComfyIcon from '@mui/icons-material/ViewComfy'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import SettingsIcon from '@mui/icons-material/Settings'
import VideocamIcon from '@mui/icons-material/Videocam'
import VideocamOffOutlinedIcon from '@mui/icons-material/VideocamOffOutlined'
import CameraPreview from '../JoinSession/CameraPreview'
import { LSButton } from '../../LiveSessionPageStyles'
import {
  AGORA_RTC_LAYOUTS,
  normalizeAgoraRtcLayout,
  preloadRtcProvidersByRoutingDecision,
  SessionSupportDock,
  useSessionSupportShell,
} from 'src/live'
import { isPatientVideoRole } from '../../utils/liveSessionRoles'

const LegacyAgoraVideoComponent = lazy(() =>
  import('src/consumer/components/PublicLiveSessionPage/components/AgoraVideoComponent')
)
const StandardLiveRtcVideoSurface = lazy(() =>
  import('src/live/shell/components/StandardLiveRtcVideoSurface')
)

const VideoCallLoadingFallback = () => (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      gap: 2,
      color: '#ffffff',
      zIndex: 2,
    }}
  >
    <CircularProgress size={40} sx={{ color: '#8b5cf6' }} />
    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
      Connecting to video call...
    </Typography>
  </Box>
)

const VideoCallContainer = ({
  userRole,
  joinAgoraCall,
  agoraRtcProps,
  agoraRtmProps,
  onCameraClose,
  onCameraOpen,
  onAudioClose,
  onAudioOpen,
  handleLeaveRoom,
  handleEndVideoCall,
  showRejoin = false,
  screenWidth,
  menuItemsList,
  selectedMenuItem,
  setSelectedMenuItem,
  renderWhatINeed,
  drawerForMoreOptionsOpenMobile,
  setDrawerForMoreOptionsOpenMobile,
  chatProps,
  joinAgoraCallFunction,
  hasTopTabs = false,
  // New props for waiting drawer
  bookedEventInfo,
  auxiliaryData,
  peerChatId,
  peerChatName,
  peerChatImage,
  changeLayoutFunction,
  // Device permissions props for camera preview
  selectedCamera,
  selectedMicrophone,
  userHasAllowedAudio,
  userHasAllowedVideo,
  useAppOwnedRtcSurface = false,
  sessionRoutingDecision = null,
  microphoneError,
  cameraError,
  setUserHasAllowedAudio,
  setUserHasAllowedVideo,
  setGiveCameraPermissionDialogOpen,
  setGiveMicrophonePermissionDialogOpen,
  meetingSrc,
  // Available devices for settings dropdown
  availableCameras,
  availableMicrophones,
  setSelectedCamera,
  setSelectedMicrophone,
  // Support QR code from API (preferred over clinic brand)
  supportQrCodeMediaIdFromApi,
  // Practitioner mobile call chrome: the video call auto-joins on session start, so the
  // full pre-join page (camera card + "Join Video Call" + gear) collapses to a minimal
  // dark "video not connected" stage that matches the in-call look.
  mobileCallChrome = false,
}) => {
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null)
  const settingsMenuOpen = Boolean(settingsAnchorEl)
  const resolvedAgoraLayout = useMemo(
    () => normalizeAgoraRtcLayout(agoraRtcProps?.layout),
    [agoraRtcProps?.layout],
  )

  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget)
  }

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null)
  }

  const isPatient = isPatientVideoRole(userRole)
  const isVideoSurfaceActive = isPatient || Boolean(joinAgoraCall)
  const sessionSupportShell = useSessionSupportShell({
    enabled: isPatient,
    inCall: isVideoSurfaceActive,
    isPatient,
    localUid: agoraRtcProps?.uid,
    bookedEventId: bookedEventInfo?.id,
    clinicBrandId: bookedEventInfo?.clinicBrandId,
    supportQrCodeMediaId: supportQrCodeMediaIdFromApi,
    sessionStartTime: bookedEventInfo?.startTime,
    fetchEngagementConfig: true,
  })

  useEffect(() => {
    if (meetingSrc !== 'aumhum') {
      return
    }

    if (useAppOwnedRtcSurface) {
      import('src/live/shell/components/StandardLiveRtcVideoSurface').catch(() => {})
      preloadRtcProvidersByRoutingDecision(sessionRoutingDecision).catch(() => {})
      return
    }

    import('src/consumer/components/PublicLiveSessionPage/components/AgoraVideoComponent').catch(
      () => {},
    )
  }, [meetingSrc, sessionRoutingDecision, useAppOwnedRtcSurface])

  // With the new drawer system, video call always takes full width
  // Secondary content is handled by the SessionDrawer component
  
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        maxHeight: '100%', // Ensure it doesn't exceed parent
        width: '100%',
        overflow: 'hidden', // Prevent scrolling - controls use absolute positioning
        position: 'relative',
        // Modern gradient background for the container
        background: 'linear-gradient(135deg, rgba(248, 250, 252, 1) 0%, rgba(243, 244, 255, 1) 100%)',
        '@media print': {
          height: '100% !important',
        },
      }}
      className="full-width-child-resizable-box-print-only standard-session-video-call-container"
    >
      {/* Video Call Area - Full width, secondary content handled by SessionDrawer */}
      {userRole === 'mentor' && !joinAgoraCall && mobileCallChrome ? (
        /* Call chrome: the call auto-joins on session start, so this state only shows while
           connecting, after the practitioner deliberately ended the video, or on a completed
           session revisited for the report. One quiet action; the Rx flow stays in the dock. */
        <Box
          className="hide-on-print-only"
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '14px',
            padding: '24px',
            textAlign: 'center',
            background: 'linear-gradient(160deg, #181b25 0%, #0f1118 100%)',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.14)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <VideocamOffOutlinedIcon sx={{ fontSize: 30, color: 'rgba(255, 255, 255, 0.65)' }} />
          </Box>
          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#ffffff' }}>
            Video is not connected
          </Typography>
          <Typography
            sx={{
              fontSize: '12.5px',
              color: 'rgba(255, 255, 255, 0.55)',
              lineHeight: 1.5,
              maxWidth: '260px',
              mt: '-6px',
            }}
          >
            The patient can&apos;t see or hear you. You can keep writing the report from the
            buttons below.
          </Typography>
          <Box
            component="button"
            onClick={joinAgoraCallFunction}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: 'none',
              cursor: 'pointer',
              padding: '12px 26px',
              borderRadius: '999px',
              color: '#ffffff',
              fontSize: '15px',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #10b981, #059669)',
              boxShadow: '0 8px 22px rgba(16, 185, 129, 0.35)',
              '&:hover': { background: 'linear-gradient(135deg, #0ea271, #048257)' },
            }}
          >
            <VideocamIcon sx={{ fontSize: 19 }} />
            Start video call
          </Box>
        </Box>
      ) : userRole === 'mentor' && !joinAgoraCall ? (
        <Box
          className="hide-on-print-only"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            minHeight: 0,
            overflowY: 'auto',
            background:
              'linear-gradient(135deg, rgba(248, 250, 252, 1) 0%, rgba(243, 244, 255, 1) 100%)',
            gap: '24px',
            padding: {
              xs: '16px',
              md: '24px',
            },
          }}
        >
          {/* Camera Preview - Reduced size for better Join button prominence */}
          {meetingSrc === 'aumhum' && (
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '320px', md: '360px' },
                display: 'flex',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CameraPreview
                selectedCamera={selectedCamera}
                selectedMicrophone={selectedMicrophone}
                availableCameras={availableCameras}
                availableMicrophones={availableMicrophones}
                setSelectedCamera={setSelectedCamera}
                setSelectedMicrophone={setSelectedMicrophone}
                userHasAllowedVideo={userHasAllowedVideo}
                userHasAllowedAudio={userHasAllowedAudio}
                isVideoCallScreen={meetingSrc === 'aumhum'}
                meetingSrc={meetingSrc}
                microphoneError={microphoneError}
                cameraError={cameraError}
                setUserHasAllowedAudio={setUserHasAllowedAudio}
                setUserHasAllowedVideo={setUserHasAllowedVideo}
                setGiveCameraPermissionDialogOpen={setGiveCameraPermissionDialogOpen}
                setGiveMicrophonePermissionDialogOpen={setGiveMicrophonePermissionDialogOpen}
              />
            </Box>
          )}
          
          {/* Join Button and Settings */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <LSButton 
              onClick={joinAgoraCallFunction}
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #4f46e5 100%)',
                border: 'none',
                color: 'white',
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(139, 92, 246, 0.3), 0 4px 12px rgba(99, 102, 241, 0.2)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)'
                e.target.style.boxShadow = '0 12px 32px rgba(139, 92, 246, 0.4), 0 6px 16px rgba(99, 102, 241, 0.3)'
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)'
                e.target.style.boxShadow = '0 8px 24px rgba(139, 92, 246, 0.3), 0 4px 12px rgba(99, 102, 241, 0.2)'
              }}
            >
              {showRejoin ? 'Rejoin Video Call' : 'Join Video Call'}
            </LSButton>
            
            {/* Settings Button */}
            <Tooltip title="Device Settings">
              <IconButton
                onClick={handleSettingsClick}
                sx={{
                  color: '#6366f1',
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  border: '1px solid rgba(99, 102, 241, 0.2)',
                  padding: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(99, 102, 241, 0.2)',
                    borderColor: 'rgba(99, 102, 241, 0.4)',
                  },
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Settings Menu */}
          <Menu
            anchorEl={settingsAnchorEl}
            open={settingsMenuOpen}
            onClose={handleSettingsClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 240,
                maxWidth: 280,
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), 0 1px 4px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(99, 102, 241, 0.1)',
                overflow: 'hidden',
              },
            }}
          >
            <Box sx={{ p: 1.5 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontWeight: 600, 
                  mb: 1.5, 
                  color: '#6366f1',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                }}
              >
                Device Settings
              </Typography>
              
              {/* Camera Selection */}
              {availableCameras && availableCameras.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#6b7280', 
                      mb: 0.75, 
                      display: 'block', 
                      fontWeight: 500,
                      fontSize: '11px',
                    }}
                  >
                    Camera
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ m: 0 }}>
                    <Select
                      value={selectedCamera || ''}
                      onChange={(e) => {
                        setSelectedCamera(e.target.value)
                      }}
                      displayEmpty
                      inputProps={{ 'aria-label': 'Select camera' }}
                      sx={{
                        height: '32px',
                        fontSize: '13px',
                        '& .MuiSelect-select': {
                          py: 0.75,
                          px: 1.25,
                        },
                      }}
                    >
                      {availableCameras.map((option) => (
                        <MenuItem 
                          key={option?.deviceId} 
                          value={option?.deviceId}
                          sx={{
                            fontSize: '13px',
                            py: 0.75,
                            minHeight: '36px',
                          }}
                        >
                          {option?.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              
              {/* Microphone Selection */}
              {availableMicrophones && availableMicrophones.length > 0 && (
                <Box>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#6b7280', 
                      mb: 0.75, 
                      display: 'block', 
                      fontWeight: 500,
                      fontSize: '11px',
                    }}
                  >
                    Microphone
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ m: 0 }}>
                    <Select
                      value={selectedMicrophone || ''}
                      onChange={(e) => {
                        setSelectedMicrophone(e.target.value)
                      }}
                      displayEmpty
                      inputProps={{ 'aria-label': 'Select microphone' }}
                      sx={{
                        height: '32px',
                        fontSize: '13px',
                        '& .MuiSelect-select': {
                          py: 0.75,
                          px: 1.25,
                        },
                      }}
                    >
                      {availableMicrophones.map((option) => (
                        <MenuItem 
                          key={option?.deviceId} 
                          value={option?.deviceId}
                          sx={{
                            fontSize: '13px',
                            py: 0.75,
                            minHeight: '36px',
                          }}
                        >
                          {option?.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              
              {(!availableCameras || availableCameras.length === 0) && 
               (!availableMicrophones || availableMicrophones.length === 0) && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#9ca3af', 
                    fontStyle: 'italic',
                    fontSize: '11px',
                    display: 'block',
                    textAlign: 'center',
                    py: 1,
                  }}
                >
                  No devices available
                </Typography>
              )}
            </Box>
          </Menu>
        </Box>
      ) : (
        <>
          <Suspense fallback={<VideoCallLoadingFallback />}>
            {useAppOwnedRtcSurface ? (
              <StandardLiveRtcVideoSurface
                sessionRoutingDecision={sessionRoutingDecision}
                agoraRtcProps={agoraRtcProps}
                agoraRtmProps={agoraRtmProps}
                handleLeaveRoom={handleEndVideoCall || handleLeaveRoom}
                bookedEventInfo={bookedEventInfo}
                auxiliaryData={auxiliaryData}
                userRole={userRole}
                peerChatId={peerChatId || chatProps?.peerChatId}
                peerChatName={peerChatName || chatProps?.peerChatName}
                peerChatImage={peerChatImage || chatProps?.peerChatImage}
                onCameraClose={onCameraClose}
                onCameraOpen={onCameraOpen}
                onAudioClose={onAudioClose}
                onAudioOpen={onAudioOpen}
                onUserJoin={sessionSupportShell.handleRemoteUserJoin}
                onUserLeave={sessionSupportShell.handleRemoteUserLeave}
                isFullHeight
                providerFallback={<VideoCallLoadingFallback />}
              />
            ) : (
              <LegacyAgoraVideoComponent
                agoraRtcProps={agoraRtcProps}
                agoraRtmProps={agoraRtmProps}
                handleLeaveRoom={handleEndVideoCall || handleLeaveRoom}
                onCameraClose={onCameraClose}
                onCameraOpen={onCameraOpen}
                onAudioClose={onAudioClose}
                onAudioOpen={onAudioOpen}
                onUserJoin={sessionSupportShell.handleRemoteUserJoin}
                onUserLeave={sessionSupportShell.handleRemoteUserLeave}
                isFullHeight
              />
            )}
          </Suspense>
          
          {/* Layout Toggle Button - Desktop only, positioned at bottom left */}
          {!useAppOwnedRtcSurface && changeLayoutFunction && joinAgoraCall && (
            <Tooltip 
              title={
                resolvedAgoraLayout === AGORA_RTC_LAYOUTS.grid
                  ? 'Switch to Pin Layout'
                  : 'Switch to Grid Layout'
              }
              arrow
            >
              <IconButton
                onClick={changeLayoutFunction}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: 16,
                  zIndex: 10001,
                  backgroundColor: 'rgba(103, 121, 227, 0.2)',
                  backdropFilter: 'blur(12px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                  border: '1px solid rgba(103, 121, 227, 0.4)',
                  color: '#6779e3',
                  width: '40px',
                  height: '40px',
                  boxShadow: '0 4px 16px rgba(103, 121, 227, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(103, 121, 227, 0.3)',
                    borderColor: 'rgba(103, 121, 227, 0.6)',
                    transform: 'scale(1.1)',
                    boxShadow: '0 6px 20px rgba(103, 121, 227, 0.3)',
                  },
                  transition: 'all 0.2s ease',
                  '@media (max-width: 768px)': {
                    width: '36px',
                    height: '36px',
                    bottom: 80, // Position above the main controls
                    left: 16,
                  },
                }}
              >
                {resolvedAgoraLayout === AGORA_RTC_LAYOUTS.grid ? (
                  <ViewComfyIcon sx={{ fontSize: 20 }} />
                ) : (
                  <ViewModuleIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
          
          {/* Waiting/Support Drawer - Always open for patients in call, can be collapsed */}
          <SessionSupportDock
            renderDrawer={sessionSupportShell.showDrawer}
            drawerOpen={sessionSupportShell.showDrawer}
            showCollapsedStrip={sessionSupportShell.showCollapsedStrip}
            onCollapse={() => {
              sessionSupportShell.setIsDrawerCollapsed(true)
            }}
            onExpand={() => {
              sessionSupportShell.setIsDrawerCollapsed(false)
            }}
            sessionStartTime={bookedEventInfo?.startTime}
            mentorName={peerChatName || bookedEventInfo?.mentorName}
            mentorProfilePicId={
              bookedEventInfo?.mentorProfilePicId || bookedEventInfo?.mentorProfilePic || peerChatImage
            }
            hasMentorJoined={sessionSupportShell.hasMentorJoined}
            showOnlySupport={sessionSupportShell.showOnlySupport}
            isCollapsed={sessionSupportShell.isDrawerCollapsed}
            supportQRCodeMediaId={sessionSupportShell.supportQRCodeMediaId}
            engagementConfig={sessionSupportShell.engagementConfig}
            bookedEventId={bookedEventInfo?.id}
          />
        </>
      )}
    </Box>
  )
}

export default VideoCallContainer
